import type { ApiRequest, ApiResponse } from '@/api/transport';
import { mockVideos } from '@/mocks/data';
import {
  MOCK_TOKEN,
  buildApiUser,
  canvasIdsForVideo,
  getState,
  patchState,
  detailViewsQuota,
  registerDetailView,
} from './state';

export type Handler = (ctx: {
  req: ApiRequest;
  params: Record<string, string>;
  query: URLSearchParams;
  body: Record<string, unknown>;
}) => ApiResponse;

interface Route {
  method: ApiRequest['method'];
  pattern: RegExp;
  keys: string[];
  handler: Handler;
}

const routes: Route[] = [];

/** Регистрирует маршрут: `/canvases/:id` → RegExp с именованными сегментами. */
export function route(method: ApiRequest['method'], path: string, handler: Handler): void {
  const keys: string[] = [];
  const pattern = new RegExp(
    '^' +
      path.replace(/\/:([a-zA-Z_]+)/g, (_m, key: string) => {
        keys.push(key);
        return '/([^/]+)';
      }) +
      '/?$',
  );
  routes.push({ method, pattern, keys, handler });
}

export function matchRoute(
  method: ApiRequest['method'],
  pathname: string,
): { handler: Handler; params: Record<string, string> } | null {
  for (const r of routes) {
    if (r.method !== method) continue;
    const m = r.pattern.exec(pathname);
    if (!m) continue;
    const params: Record<string, string> = {};
    r.keys.forEach((key, i) => {
      params[key] = decodeURIComponent(m[i + 1] ?? '');
    });
    return { handler: r.handler, params };
  }
  return null;
}

const ok = (body: unknown, status = 200): ApiResponse => ({ status, body });
const fail = (
  status: number,
  message: string,
  error?: string,
  extra: Record<string, unknown> = {},
): ApiResponse => ({
  status,
  body: { message, error, ...extra },
});

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

// ——— Аутентификация (AUTH-2…6) ———
route('POST', '/register', ({ body }) => {
  const email = str(body['email']);
  const password = str(body['password']);
  const name = str(body['name']);
  if (!email.includes('@')) return fail(422, 'The email field must be a valid email address.');
  if (password.length < 8) return fail(422, 'The password field must be at least 8 characters.');
  if (!name.trim()) return fail(422, 'The name field is required.');
  // Новая регистрация: гид ещё не пройден (ONB-1).
  patchState({
    pendingEmails: [...getState().pendingEmails, email],
    verified: false,
    onboardingCompletedAt: null,
  });
  return ok(
    {
      message: 'Registered. Verification code sent.',
      user: buildApiUser({ email, name, email_verified_at: null }),
    },
    201,
  );
});

route('POST', '/login', ({ body }) => {
  const email = str(body['email']);
  const password = str(body['password']);
  if (!email.includes('@') || password.length < 8) return fail(401, 'Invalid email or password');
  if (getState().pendingEmails.includes(email) && !getState().verified) {
    return fail(403, 'Email is not verified yet. Please confirm your email before logging in.');
  }
  patchState({ verified: true });
  return ok({ access_token: MOCK_TOKEN, token_type: 'Bearer', user: buildApiUser({ email }) });
});

route('POST', '/email-verification/send-code', () => ok({ message: 'Code sent' }));

route('POST', '/email-verification/verify-code', ({ body }) => {
  const code = str(body['code']);
  if (!/^\d{6}$/.test(code)) return fail(422, 'Invalid or expired verification code');
  patchState({ verified: true, pendingEmails: [] });
  return ok({
    access_token: MOCK_TOKEN,
    token_type: 'Bearer',
    user: buildApiUser({ email: str(body['email']) || undefined }),
  });
});

route('POST', '/auth/password/forgot', () =>
  ok({ ok: true, message: 'If an account exists for this address, a code is on its way.' }),
);

route('POST', '/auth/password/reset', ({ body }) => {
  if (!/^\d{6}$/.test(str(body['code']))) return fail(422, 'Invalid or expired code');
  if (str(body['password']).length < 8)
    return fail(422, 'The password field must be at least 8 characters.');
  return ok({ ok: true, message: 'Password updated' });
});

route('POST', '/auth/google', () => {
  // В mock вход через Google — новый пользователь: гид сразу (ONB-1).
  patchState({ verified: true, onboardingCompletedAt: null });
  return ok({ access_token: MOCK_TOKEN, token_type: 'Bearer', user: buildApiUser() });
});

route('POST', '/logout', () => ok({ message: 'Logged out' }));

route('GET', '/me', ({ req }) => {
  const auth = req.headers['Authorization'] ?? '';
  if (!auth.startsWith('Bearer ')) return fail(401, 'Unauthenticated.');
  return ok({ user: buildApiUser() });
});

route('POST', '/me/welcome-shown', () => {
  patchState({ welcomeShownAt: new Date().toISOString() });
  return ok({ ok: true });
});

// B6: флаг онбординга
route('POST', '/me/onboarding-complete', () => {
  const at = getState().onboardingCompletedAt ?? new Date().toISOString();
  patchState({ onboardingCompletedAt: at });
  return ok({ ok: true, onboarding_completed_at: at });
});

// Бесплатный тариф: дневная квота детальных просмотров (config/free_tier.php на бэкенде)
route('POST', '/me/detail-views', ({ body }) => {
  if (getState().fullAccess) return ok({ ok: true, quota: null });
  const videoId = Number(body['video_reference_id']);
  if (!registerDetailView(videoId))
    return fail(
      403,
      'Daily limit of detailed views on the free plan is reached',
      'detail_quota_exceeded',
      {
        quota: detailViewsQuota(),
      },
    );
  return ok({ ok: true, quota: detailViewsQuota() });
});

// ——— Телеметрия ———
route('POST', '/landing-event', () => ok({ ok: true }, 202));
route('POST', '/landing-events/batch', () => ok({ ok: true }, 202));
route('POST', '/guest-visit', () => ok({ ok: true }));
route('POST', '/heartbeat', () => ok({ ok: true }));
route('GET', '/clarity-config', () => ok({ clarity: { project_id: null } }));
route('GET', '/crisp-config', () => ok({ crisp: { enabled: false } }));

// ——— Закладки (Saved) — эндпоинт добавляется вместе с B1 ———
route('GET', '/bookmarks', () => ok({ video_ids: getState().bookmarks }));
route('GET', '/bookmarks/videos', () => {
  const ids = getState().bookmarks;
  const byId = new Map(mockVideos.map((v) => [v.id, v]));
  const data = ids
    .map((id) => byId.get(id))
    .filter((v): v is (typeof mockVideos)[number] => !!v)
    .map((v) => {
      const canvasIds = canvasIdsForVideo(v.id);
      return {
        ...v,
        saved_canvas_ids: canvasIds,
        is_saved: canvasIds.length > 0,
        saved_title: getState().bookmarkTitles?.[String(v.id)] ?? null,
      };
    });
  return ok({ data });
});
route('POST', '/bookmarks', ({ body }) => {
  const id = Number(body['video_reference_id']);
  if (!Number.isFinite(id)) return fail(422, 'video_reference_id is required');
  const next = getState().bookmarks.includes(id)
    ? getState().bookmarks
    : [...getState().bookmarks, id];
  patchState({ bookmarks: next });
  return ok({ video_ids: next }, 201);
});
route('PATCH', '/bookmarks/:id', ({ params, body }) => {
  const id = Number(params['id']);
  if (!getState().bookmarks.includes(id))
    return fail(404, 'This video is not in Saved', 'bookmark_not_found');
  if (!('title' in body)) return fail(422, 'The title field must be present.');
  const raw = body['title'];
  if (raw !== null && typeof raw !== 'string') return fail(422, 'The title must be a string.');
  const title = (raw ?? '').trim() || null;
  if (title && title.length > 120)
    return fail(422, 'The title may not be greater than 120 characters.');
  const titles = { ...(getState().bookmarkTitles ?? {}) };
  if (title) titles[String(id)] = title;
  else delete titles[String(id)];
  patchState({ bookmarkTitles: titles });
  return ok({ video_reference_id: id, title });
});
route('DELETE', '/bookmarks/:id', ({ params }) => {
  const id = Number(params['id']);
  const titles = { ...(getState().bookmarkTitles ?? {}) };
  delete titles[String(id)];
  patchState({ bookmarks: getState().bookmarks.filter((x) => x !== id), bookmarkTitles: titles });
  return ok({ video_ids: getState().bookmarks });
});
